import { fetchSessionFromApi } from './api';
import { decodeToken, resolveToken } from './jwt';
import type { AuthConfig, IbxPayload, StarkSessionResponse, UserSessionInfo } from './types';

/** Stark /api/session returned 500/502/503/504 or was unreachable. Not the same as 401 or a null session. */
export class SessionServiceUnavailableError extends Error {
  // Default only for a pure network failure reaching Stark itself — no body
  // to read a message from. Same text checkout-api/wakanda-api already use.
  constructor(
    message = 'An unexpected error occurred. Please try again later or call us at +91 9650660070 for quick assistance.',
  ) {
    super(message);
    this.name = 'SessionServiceUnavailableError';
  }
}

// Same list Stark uses for "{service} is not available".
const SERVICE_UNAVAILABLE_STATUSES: ReadonlySet<number> = new Set([500, 502, 503, 504]);

/**
 * @repo/http-client throws Error("HTTP <status>: <body>") on non-2xx.
 * `message` is only used from Stark's standard body (it carries `service`), never raw error text.
 */
function parseHttpFailure(err: unknown): { status: number | null; message: string | null } {
  if (!(err instanceof Error)) return { status: null, message: null };
  const match = err.message.match(/^HTTP (\d+): (.*)$/s);
  if (!match) return { status: null, message: null };
  const status = Number(match[1]);
  try {
    const body = JSON.parse(match[2] ?? '') as { service?: string; message?: string };
    return { status, message: body?.service ? (body.message ?? null) : null };
  } catch {
    return { status, message: null };
  }
}

/** fetch() rejects with TypeError when Stark can't be reached at all. */
function isNetworkFailure(err: unknown): boolean {
  return err instanceof TypeError;
}

// Guarded so this module doesn't throw when bundled into a runtime with no
// `process` global (e.g. wakanda-ui's Angular build) — those runtimes call
// configureAuth() explicitly with their own env mechanism anyway.
let _starkUrl = typeof process !== 'undefined' ? (process.env.NEXT_PUBLIC_API_URL ?? '') : '';

export function configureAuth(config: AuthConfig): void {
  _starkUrl = config.starkUrl;
}

/** @param explicitToken Optional token that takes priority over
 *  the `ibx` cookie, for callers without cookie access. 
 */
export async function getUserSessionInfo(explicitToken?: string): Promise<UserSessionInfo | null> {
  const token = explicitToken ?? resolveToken();

  if (!token) {
    throw new Error('Unauthorized');
  }

  let payload: IbxPayload;
  try {
    payload = await decodeToken(token);
  } catch (err) {
    // Corrupt token: a client problem, not an outage.
    console.error('[getUserSessionInfo] decodeToken error:', err);
    return null;
  }

  const userId: number | null = payload.userId ?? null;
  const profileId: number | null = payload.profileId ?? null;

  if (userId === null && profileId === null) {
    return {
      userId: null,
      profileId: null,
      isLoggedIn: false,
      cart: {
        id: payload.cartId ?? null,
        count: payload.cartCount ?? 0,
      },
    };
  }

  try {
    // fetchSessionFromApi's declared return type (UserSessionInfo) doesn't
    // match stark's actual response envelope ({ user: UserSessionInfo | null
    // }). get<T>() only performs a compile-time cast, no runtime check, so
    // this reinterpretation is safe. Handled here rather than in api.ts —
    // see StarkSessionResponse in types.ts.
    const response = (await fetchSessionFromApi(_starkUrl, token)) as unknown as StarkSessionResponse | null;
    return response?.user ?? null;
  } catch (err) {
    const { status, message } = parseHttpFailure(err);
    if (status === 401) {
      throw new Error('Unauthorized');
    }
    if ((status !== null && SERVICE_UNAVAILABLE_STATUSES.has(status)) || (status === null && isNetworkFailure(err))) {
      throw new SessionServiceUnavailableError(message ?? undefined);
    }
    // Other failures (4xx, bad JSON): log, treat as no session.
    console.error('[getUserSessionInfo] session request failed:', err);
    return null;
  }
}
